import type { CompositionPoint } from '../../lib/dashboard/composition';
import { CoachCard } from '../concept/coach-card';

const SEGMENTS = [
  { key: 'fat', label: 'Fat mass', swatch: 'bg-foreground' },
  { key: 'muscle', label: 'Muscle mass', swatch: 'bg-primary' },
  { key: 'other', label: 'Other lean', swatch: 'bg-[hsl(var(--lean-pale))]' },
  { key: 'lean', label: 'Lean mass', swatch: 'bg-primary' },
] as const;

/**
 * Part-to-whole, dark to light. A sequential encoding rather than a categorical
 * one, which is why near-monochrome is correct here.
 *
 * Every row is directly labelled. That is load-bearing: --lean-pale is 1.55:1
 * against the card, so the label is what carries the identity, not the colour.
 */
export const MassBar: React.FC<{
  point: CompositionPoint;
  /**
   * Split lean into muscle and other lean. Muscle mass is an estimate the
   * Sizewall team has not verified, so it shows only where the experience
   * says so (Config.showMuscleMass, dev only). Otherwise lean is one figure:
   * fat against everything that is not fat.
   */
  showMuscle?: boolean;
}> = ({ point, showMuscle = false }) => {
  // The muscle split also needs the client's age. Without one, lean is shown
  // whole rather than as a 0 kg muscle row beside a lean row that holds it all.
  const { muscleKg, musclePercent, otherKg, otherPercent } = point;
  const split =
    muscleKg !== undefined &&
    musclePercent !== undefined &&
    otherKg !== undefined &&
    otherPercent !== undefined;
  const total = point.fatKg + point.leanKg;
  const rows = [
    { ...SEGMENTS[0], kg: point.fatKg, percent: point.fatPercent },
    ...(showMuscle && split
      ? [
          { ...SEGMENTS[1], kg: muscleKg, percent: musclePercent },
          { ...SEGMENTS[2], kg: otherKg, percent: otherPercent },
        ]
      : [{ ...SEGMENTS[3], kg: point.leanKg, percent: 100 - point.fatPercent }]),
  ];

  return (
    <CoachCard
      testId="composition-bar"
      title={`Composition · ${point.date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`}
      provenance={
        !showMuscle
          ? 'Fat from the tape body-fat estimate · lean is the rest of the weight'
          : split
            ? 'Fat from the tape body-fat estimate · muscle via Lee 2000 from lean mass'
            : "Fat from the tape body-fat estimate · muscle is split from lean once the client's age is set"
      }
    >
      <div className="flex h-3.5 gap-0.5 overflow-hidden rounded-sm">
        {rows.map((r) => (
          <div
            key={r.key}
            className={r.swatch}
            style={{ width: `${(r.kg / total) * 100}%` }}
          />
        ))}
      </div>
      <div className="grid grid-cols-[1fr_auto_auto] gap-x-3.5 gap-y-2 text-xs text-muted-foreground">
        {rows.map((r) => (
          <div key={r.key} className="contents">
            <span className="flex items-center gap-2">
              <span className={`h-2 w-2 rounded-sm ${r.swatch}`} />
              {r.label}
            </span>
            <span className="tabular font-semibold text-foreground">{r.kg.toFixed(1)} kg</span>
            <span className="tabular">{r.percent.toFixed(1)} %</span>
          </div>
        ))}
      </div>
    </CoachCard>
  );
};
